import { brand } from '../../../theme/themePrimitives';
import CustomModal from '../../modals';
import LabelWrapper from '../../wrapper/label';
import { LeadObject } from '../../../params/redux/leads';
import { settingBulkMessageFlag } from '../../../redux/slices/leads';
import { useState } from 'react';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { ChatBubbleOutlineOutlined, EmailOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: AddPipelineModal
 * @paragraph: A modal component for adding a new pipeline. Uses Formik for form handling
 * and integrates with Redux for pipeline creation.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const BulkMessagesModal = ({ open, onClose, selectedRows }: { open: boolean; onClose: () => void, selectedRows: number[] }) => {
    /**
     * @heading3: Variables
     * @ol:
     * initialValues: {PipelineParams} -  Initial form values for the pipeline creation form.
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders: An object containing the loader details from the Redux store.
     */
    // Initial form values for the pipeline creation form
    const [selectedOption, setSelectedOption] = useState<'email' | 'sms' | ''>('');
    const dispatch = useDispatch<AppDispatch>();
    const { loaders, leads } = useSelector((state: RootState) => state.lead);
    const { selectedForm } = useSelector((state: RootState) => state.form);
    const { integrations } = useSelector((state: RootState) => state.integration);

    const filteredLeads = leads.filter((lead: LeadObject) => lead.form_id === selectedForm?.id);
    const selectedLeads = leads.filter((lead) => selectedRows.includes(lead.id));
    const hasPhoneNumber = selectedLeads.some((lead) => Boolean(lead.phone) && lead.phone?.trim() !== '');
    const hasEmail = selectedLeads.some((lead) => Boolean(lead.email) && lead.email?.trim() !== '');

    const onClickHandler = () => {
        dispatch(settingBulkMessageFlag({
            type: selectedOption,
            rows: selectedRows
        }));
        onClose();
    };

    const disableEmail = !(integrations.includes('email') && hasEmail);
    const disablePhone = !(integrations.includes('twilio') && hasPhoneNumber);

    return <CustomModal open={open} handleClose={loaders.isCreating ? () => null : onClose} title="Bulk Messages">
        <Box textAlign="center" mb={2}>
            <Typography sx={{ background: brand[50], borderRadius: '4px', padding: '8px', color: brand[300] }}>
                You have selected {selectedRows.length} out of {selectedForm ? filteredLeads.length : leads.length}
            </Typography>
        </Box>
        <LabelWrapper label="Select the type of bulk message you want to send" />
        <RadioGroup value={selectedOption} onChange={(e) => setSelectedOption(e.target.value as 'email' | 'sms' | '')}>
            <Box
                border={1}
                borderColor={selectedOption === 'email' ? brand[300] : 'grey.300'}
                borderRadius={1}
                py={1}
                px={2}
                mb={2}
                bgcolor={selectedOption === 'email' ? brand[50] : 'transparent'}
                color={selectedOption === 'email' ? brand[300] : 'black'}
            >
                <FormControlLabel
                    value="email"
                    disabled={disableEmail}
                    control={<Radio />}
                    label={
                        <Box display="flex" gap={2}>
                            <EmailOutlined />
                            <Typography variant="body2">Email</Typography>
                        </Box>
                    }
                />
            </Box>
            <Box
                border={1}
                borderColor={selectedOption === 'sms' ? brand[300] : 'grey.300'}
                borderRadius={1}
                py={1}
                px={2}
                bgcolor={selectedOption === 'sms' ? brand[50] : 'transparent'}
                color={selectedOption === 'sms' ? brand[300] : 'black'}
            >
                <FormControlLabel
                    value="sms"
                    disabled={disablePhone}
                    control={<Radio />}
                    label={
                        <Box display="flex" gap={2}>
                            <ChatBubbleOutlineOutlined />
                            <Typography variant="body2">SMS</Typography>
                        </Box>
                    }
                />
            </Box>
        </RadioGroup>
        <Box display="flex" gap={2} mt={3}>
            <Button variant="outlined" color="secondary" onClick={onClose} fullWidth>
                Cancel
            </Button>
            <Button variant="contained" color="primary" disabled={selectedOption === ''} fullWidth onClick={onClickHandler}>
                Confirm
            </Button>
        </Box>
    </CustomModal>;
};

export default BulkMessagesModal;